import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ajaxCall from '../utils/ajaxcall';
import config from '../config';
import {
	clearAuth,
	getOrgInfoFromToken,
	getToken,
	isAuthenticated,
	setToken as persistToken,
} from '../utils/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
	const [token, setTokenState] = useState(() => {
		const stored = getToken();
		return isAuthenticated(stored) ? stored : null;
	});
	const [orgInfo, setOrgInfo] = useState(() => {
		const stored = getToken();
		return getOrgInfoFromToken(stored) || { user: '', domain: '', roleId: '', orgId: '', orgName: '' };
	});
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const stored = getToken();
		if (!stored) {
			setLoading(false);
			return;
		}

		const info = getOrgInfoFromToken(stored);
		if (!info) {
			clearAuth();
			setTokenState(null);
			setOrgInfo({ user: '', domain: '', roleId: '', orgId: '', orgName: '' });
			setLoading(false);
			return;
		}

		setOrgInfo(info);
		setTokenState(stored);
		setLoading(false);
	}, []);

	const login = useCallback(async (email, password) => {
		const response = await ajaxCall.postCall(`${config.api.generic}/account/login`, {
			email: email.trim(),
			password,
		});

		if (!response?.status) {
			const errorMessage = typeof response?.context?.message === 'string'
				? response.context.message
				: 'Invalid credentials. Please try again.';
			return {
				success: false,
				message: errorMessage,
			};
		}

		const responseData = response.context?.message?.data;
		const newToken = Array.isArray(responseData) ? responseData[0] : null;

		if (!newToken || !isAuthenticated(newToken)) {
			return {
				success: false,
				message: 'Login failed. Please try again.',
			};
		}

		persistToken(newToken);
		setTokenState(newToken);
		setOrgInfo(getOrgInfoFromToken(newToken));

		return { success: true };
	}, []);

	const logout = useCallback(async () => {
		if (token) {
			try {
				await ajaxCall.postCall(`${config.api.generic}/account/logout`, {}, token);
			} catch {
				// Logout is client-side; API call is best-effort.
			}
		}
		clearAuth();
		setTokenState(null);
		setOrgInfo({ user: '', domain: '', roleId: '', orgId: '', orgName: '' });
	}, [token]);

	const value = useMemo(
		() => ({
			token,
			orgInfo,
			loading,
			isAuthenticated: Boolean(token),
			login,
			logout,
		}),
		[token, orgInfo, loading, login, logout]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within AuthProvider');
	}
	return context;
};
